/**
 * Дашборд пациента.
 *
 * Отображает сводную информацию:
 * - текущий интегральный показатель состояния (ИПС)
 * - тренд (улучшение / стабильно / ухудшение)
 * - средние значения симптомов
 * - график истории ИПС
 * - флаги риска (при наличии)
 * - корреляционный анализ
 * - календарь-хитмап симптомов
 * - графики сна/активности, лекарств, тестов
 */

import React, { useEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ReferenceLine,
  ScatterChart, Scatter, BarChart, Bar, Legend,
} from 'recharts';
import { analyticsAPI } from '../../api/client';
import ChartExportWrapper from '../../components/ChartExportButton';
import { AnalyticsStatus, ScoreHistoryPoint, CorrelationResult, HeatmapDay } from '../../types';

interface ForecastPoint {
  date: string;
  score: number;
}

interface ExtendedData {
  sleep_activity: { date: string; sleep: number | null; activity: number | null }[];
  medication_adherence: { date: string; adherence_pct: number }[];
  test_progress: { test_name: string; scores: { date: string; normalized_score: number }[] }[];
}

/** Цвета для отображения тренда */
const TREND_COLORS: Record<string, string> = {
  improving: '#16a34a',
  stable: '#2563eb',
  worsening: '#f59e0b',
  critical: '#dc2626',
};

/** Названия флагов риска для отображения */
const RISK_FLAG_LABELS: Record<string, string> = {
  high_score: 'Высокий ИПС',
  worsening_trend: 'Ухудшение динамики',
  extreme_symptom: 'Экстремальный симптом',
  missed_medication: 'Пропуск лекарств',
};

const MONTH_NAMES = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

const DAY_NAMES = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const Dashboard: React.FC = () => {
  const [status, setStatus] = useState<AnalyticsStatus | null>(null);
  const [history, setHistory] = useState<ScoreHistoryPoint[]>([]);
  const [forecast, setForecast] = useState<ForecastPoint[]>([]);
  const [recommendations, setRecommendations] = useState<{ type: string; text: string }[]>([]);
  const [correlations, setCorrelations] = useState<CorrelationResult[]>([]);
  const [heatmapDays, setHeatmapDays] = useState<HeatmapDay[]>([]);
  const [heatmapYear, setHeatmapYear] = useState(new Date().getFullYear());
  const [heatmapMonth, setHeatmapMonth] = useState(new Date().getMonth() + 1);
  const [extendedData, setExtendedData] = useState<ExtendedData | null>(null);
  const [loading, setLoading] = useState(true);
  const [hoveredDay, setHoveredDay] = useState<HeatmapDay | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const [statusRes, historyRes, forecastRes, recsRes, corrRes, extRes] = await Promise.all([
          analyticsAPI.getStatus(),
          analyticsAPI.getHistory(12),
          analyticsAPI.getForecast(2),
          analyticsAPI.getRecommendations(),
          analyticsAPI.getCorrelations(30),
          analyticsAPI.getDashboardExtended(30),
        ]);
        if (cancelled) return;
        setStatus(statusRes.data);
        setHistory(historyRes.data.history);
        setForecast(forecastRes.data.forecast || []);
        setRecommendations(recsRes.data.recommendations || []);
        setCorrelations(corrRes.data.correlations || []);
        setExtendedData(extRes.data);
      } catch (err) {
        if (!cancelled) console.error('Ошибка загрузки данных дашборда:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadHeatmap = async () => {
      try {
        const res = await analyticsAPI.getHeatmap(heatmapYear, heatmapMonth);
        if (!cancelled) setHeatmapDays(res.data.days);
      } catch (err) {
        if (!cancelled) console.error('Ошибка загрузки хитмапа:', err);
      }
    };
    loadHeatmap();
    return () => { cancelled = true; };
  }, [heatmapYear, heatmapMonth]);

  const handlePrevMonth = () => {
    if (heatmapMonth === 1) {
      setHeatmapYear(heatmapYear - 1);
      setHeatmapMonth(12);
    } else {
      setHeatmapMonth(heatmapMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (heatmapMonth === 12) {
      setHeatmapYear(heatmapYear + 1);
      setHeatmapMonth(1);
    } else {
      setHeatmapMonth(heatmapMonth + 1);
    }
  };

  if (loading) return <div style={{ padding: 40 }}>Загрузка...</div>;
  if (!status) return <div style={{ padding: 40 }}>Нет данных для отображения. Заполните дневник.</div>;

  // Подготовка данных для радарного графика симптомов
  const radarData = Object.entries(status.symptom_averages).map(([name, value]) => ({
    symptom: name,
    value: value,
  }));

  // Хитмап: формируем сетку недель
  const getHeatmapGrid = () => {
    if (heatmapDays.length === 0) return [];
    const firstDay = new Date(heatmapDays[0].date);
    let startDow = firstDay.getDay(); // 0=Sun
    startDow = startDow === 0 ? 6 : startDow - 1; // Convert to Mon=0

    const grid: (HeatmapDay | null)[][] = [];
    let week: (HeatmapDay | null)[] = Array(startDow).fill(null);

    for (const day of heatmapDays) {
      week.push(day);
      if (week.length === 7) {
        grid.push(week);
        week = [];
      }
    }
    if (week.length > 0) {
      while (week.length < 7) week.push(null);
      grid.push(week);
    }
    return grid;
  };

  const getHeatmapColor = (day: HeatmapDay | null) => {
    if (!day || !day.has_report) return '#e5e7eb';
    const val = day.overall_feeling;
    if (val === null) return '#e5e7eb';
    if (val <= 3) return '#22c55e';
    if (val <= 6) return '#facc15';
    return '#ef4444';
  };

  return (
    <div>
      <h1 style={{ marginBottom: 24 }}>Мой дашборд</h1>

      {/* Карточки показателей */}
      <div style={styles.cards}>
        <div style={styles.card}>
          <div style={styles.cardLabel}>Интегральный показатель</div>
          <div style={{
            ...styles.cardValue,
            color: status.integrated_score >= 7 ? '#dc2626' :
                   status.integrated_score >= 4 ? '#f59e0b' : '#16a34a',
          }}>
            {status.integrated_score.toFixed(1)}
          </div>
          <div style={styles.cardHint}>из 10.0</div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardLabel}>Тренд</div>
          <div style={{
            ...styles.cardValue,
            color: TREND_COLORS[status.trend] || '#333',
            fontSize: 20,
          }}>
            {status.trend_ru}
          </div>
          <div style={styles.cardHint}>
            {status.trend_delta > 0 ? '+' : ''}{status.trend_delta.toFixed(1)} за неделю
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardLabel}>Период анализа</div>
          <div style={{ ...styles.cardValue, fontSize: 16 }}>
            {status.period.from} — {status.period.to}
          </div>
          <div style={styles.cardHint}>последние 7 дней</div>
        </div>
      </div>

      {/* Флаги риска */}
      {status.risk_flags.length > 0 && (
        <div style={styles.riskBanner}>
          <strong>Внимание! Обнаружены факторы риска:</strong>
          <ul style={{ margin: '8px 0 0 20px' }}>
            {status.risk_flags.map((flag) => (
              <li key={flag}>{RISK_FLAG_LABELS[flag] || flag}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Рекомендации */}
      {recommendations.length > 0 && (
        <div style={styles.recsCard}>
          <h3 style={styles.chartTitle}>Рекомендации</h3>
          {recommendations.map((rec, i) => (
            <div key={i} style={{
              ...styles.recItem,
              backgroundColor: rec.type === 'warning' ? '#fffbeb' :
                              rec.type === 'success' ? '#f0fdf4' : '#eff6ff',
              borderLeft: `4px solid ${
                rec.type === 'warning' ? '#f59e0b' :
                rec.type === 'success' ? '#16a34a' : '#2563eb'
              }`,
            }}>
              {rec.text}
            </div>
          ))}
        </div>
      )}

      {/* Календарь-хитмап */}
      <div style={styles.chartCard}>
        <h3 style={styles.chartTitle}>Календарь самочувствия</h3>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <button style={styles.navBtn} onClick={handlePrevMonth}>&larr;</button>
          <span style={{ fontSize: 16, fontWeight: 600 }}>
            {MONTH_NAMES[heatmapMonth - 1]} {heatmapYear}
          </span>
          <button style={styles.navBtn} onClick={handleNextMonth}>&rarr;</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
          {DAY_NAMES.map(d => (
            <div key={d} style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-secondary)', paddingBottom: 4 }}>{d}</div>
          ))}
          {getHeatmapGrid().flat().map((day, idx) => (
            <div
              key={idx}
              style={{
                aspectRatio: '1',
                backgroundColor: getHeatmapColor(day),
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                color: day && day.has_report ? '#fff' : '#9ca3af',
                cursor: day ? 'pointer' : 'default',
                position: 'relative',
              }}
              onMouseEnter={() => day && setHoveredDay(day)}
              onMouseLeave={() => setHoveredDay(null)}
            >
              {day ? new Date(day.date).getDate() : ''}
            </div>
          ))}
        </div>
        {hoveredDay && (
          <div style={styles.tooltip}>
            <strong>{hoveredDay.date}</strong>
            {hoveredDay.has_report ? (
              <>
                <div>Самочувствие: {hoveredDay.overall_feeling}/10</div>
                <div>Симптомы: {hoveredDay.symptom_score}/10</div>
              </>
            ) : (
              <div>Нет отчёта</div>
            )}
          </div>
        )}
        <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 11, color: 'var(--text-secondary)' }}>
          <span><span style={{ display: 'inline-block', width: 12, height: 12, backgroundColor: '#22c55e', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />Хорошо (1-3)</span>
          <span><span style={{ display: 'inline-block', width: 12, height: 12, backgroundColor: '#facc15', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />Средне (4-6)</span>
          <span><span style={{ display: 'inline-block', width: 12, height: 12, backgroundColor: '#ef4444', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />Плохо (7-10)</span>
          <span><span style={{ display: 'inline-block', width: 12, height: 12, backgroundColor: '#e5e7eb', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />Нет данных</span>
        </div>
      </div>

      {/* Графики */}
      <div style={styles.chartsRow}>
        {/* График истории ИПС с прогнозом */}
        <div style={styles.chartCard}>
          <ChartExportWrapper fileName="ips_dynamics">
            <h3 style={styles.chartTitle}>
              Динамика ИПС (12 недель)
              {forecast.length > 0 && <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 8 }}>+ прогноз</span>}
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={[
                ...history.map(h => ({ ...h, forecast: undefined as number | undefined })),
                ...forecast.map((f, i) => ({
                  date: f.date,
                  score: i === 0 && history.length > 0 ? history[history.length - 1].score : undefined as number | undefined,
                  forecast: f.score,
                  trend: 'forecast',
                })),
              ]}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis domain={[0, 10]} />
                <Tooltip />
                <ReferenceLine y={7} stroke="#dc2626" strokeDasharray="5 5" />
                <Line
                  type="monotone" dataKey="score"
                  stroke="#2563eb" strokeWidth={2}
                  dot={{ r: 4 }}
                  name="ИПС"
                  connectNulls={false}
                />
                {forecast.length > 0 && (
                  <Line
                    type="monotone" dataKey="forecast"
                    stroke="#f59e0b" strokeWidth={2}
                    strokeDasharray="6 4"
                    dot={{ r: 4 }}
                    name="Прогноз"
                    connectNulls={false}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </ChartExportWrapper>
        </div>

        {/* Радарный график симптомов */}
        {radarData.length > 0 && (
          <div style={styles.chartCard}>
            <ChartExportWrapper fileName="symptoms_radar">
              <h3 style={styles.chartTitle}>Средние симптомы за неделю</h3>
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="symptom" tick={{ fontSize: 11 }} />
                  <PolarRadiusAxis domain={[0, 10]} />
                  <Radar
                    dataKey="value" stroke="#2563eb"
                    fill="#2563eb" fillOpacity={0.3}
                    name="Выраженность"
                  />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            </ChartExportWrapper>
          </div>
        )}
      </div>

      {/* Сон и активность */}
      {extendedData && extendedData.sleep_activity.length > 0 && (
        <div style={styles.chartCard}>
          <ChartExportWrapper fileName="sleep_activity">
            <h3 style={styles.chartTitle}>Сон и активность (30 дней)</h3>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={extendedData.sleep_activity}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 10]} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="sleep" stroke="#8b5cf6" strokeWidth={2} name="Сон" dot={false} connectNulls />
                <Line type="monotone" dataKey="activity" stroke="#06b6d4" strokeWidth={2} name="Активность" dot={false} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </ChartExportWrapper>
        </div>
      )}

      {/* Приверженность лекарствам */}
      {extendedData && extendedData.medication_adherence.length > 0 && (
        <div style={styles.chartCard}>
          <ChartExportWrapper fileName="medication_adherence">
            <h3 style={styles.chartTitle}>Приверженность лекарствам (14 дней)</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={extendedData.medication_adherence}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} unit="%" />
                <Tooltip />
                <Bar dataKey="adherence_pct" fill="#16a34a" name="Приём (%)" />
              </BarChart>
            </ResponsiveContainer>
          </ChartExportWrapper>
        </div>
      )}

      {/* Прогресс тестов */}
      {extendedData && extendedData.test_progress.length > 0 && (
        <div style={styles.chartCard}>
          <ChartExportWrapper fileName="test_progress">
            <h3 style={styles.chartTitle}>Прогресс тестов</h3>
            {extendedData.test_progress.map((tp) => (
              <div key={tp.test_name} style={{ marginBottom: 16 }}>
                <h4 style={{ fontSize: 14, marginBottom: 8 }}>{tp.test_name}</h4>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={tp.scores}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 10]} />
                    <Tooltip />
                    <Bar dataKey="normalized_score" fill="#8b5cf6" name="Балл (норм.)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ))}
          </ChartExportWrapper>
        </div>
      )}

      {/* Корреляционный анализ */}
      {correlations.length > 0 && (
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Корреляционный анализ</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
            <thead>
              <tr>
                <th style={styles.th}>Показатель X</th>
                <th style={styles.th}>Показатель Y</th>
                <th style={styles.th}>Коэффициент (r)</th>
                <th style={styles.th}>Сила связи</th>
              </tr>
            </thead>
            <tbody>
              {correlations.slice(0, 10).map((c, i) => (
                <tr key={i}>
                  <td style={styles.td}>{c.x_label}</td>
                  <td style={styles.td}>{c.y_label}</td>
                  <td style={{
                    ...styles.td, fontWeight: 600,
                    color: Math.abs(c.r) >= 0.7 ? '#dc2626' : Math.abs(c.r) >= 0.4 ? '#f59e0b' : '#6b7280',
                  }}>
                    {c.r.toFixed(3)}
                  </td>
                  <td style={styles.td}>
                    {c.strength === 'strong' ? 'Сильная' : c.strength === 'moderate' ? 'Умеренная' : 'Слабая'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Scatter plot для сильнейшей корреляции */}
          {correlations[0] && (
            <ChartExportWrapper fileName="correlation_scatter">
              <h4 style={{ fontSize: 14, marginBottom: 8 }}>
                {correlations[0].x_label} vs {correlations[0].y_label} (r={correlations[0].r.toFixed(3)})
              </h4>
              <ResponsiveContainer width="100%" height={250}>
                <ScatterChart>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="x" name={correlations[0].x_label} type="number" />
                  <YAxis dataKey="y" name={correlations[0].y_label} type="number" />
                  <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                  <Scatter data={correlations[0].points} fill="#2563eb" />
                </ScatterChart>
              </ResponsiveContainer>
            </ChartExportWrapper>
          )}
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  cards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 16,
    marginBottom: 24,
  },
  card: {
    backgroundColor: 'var(--bg-card)',
    padding: 20,
    borderRadius: 12,
    boxShadow: 'var(--shadow-card)',
    textAlign: 'center',
  },
  cardLabel: { fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 },
  cardValue: { fontSize: 32, fontWeight: 700 },
  cardHint: { fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 },
  riskBanner: {
    backgroundColor: 'var(--bg-danger)',
    border: '1px solid #fecaca',
    color: '#dc2626',
    padding: 16,
    borderRadius: 12,
    marginBottom: 24,
    fontSize: 14,
  },
  chartsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: 16,
    marginBottom: 16,
  },
  chartCard: {
    backgroundColor: 'var(--bg-card)',
    padding: 20,
    borderRadius: 12,
    boxShadow: 'var(--shadow-card)',
    marginBottom: 16,
  },
  chartTitle: { fontSize: 16, marginBottom: 16, color: 'var(--text-primary)' },
  recsCard: {
    backgroundColor: 'var(--bg-card)',
    padding: 20,
    borderRadius: 12,
    boxShadow: 'var(--shadow-card)',
    marginBottom: 24,
  },
  recItem: {
    padding: '10px 14px',
    borderRadius: 8,
    fontSize: 14,
    marginBottom: 8,
    lineHeight: 1.5,
  },
  navBtn: {
    background: 'var(--bg-card)', border: '1px solid var(--border-color)',
    padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 16,
    color: 'var(--text-primary)',
  },
  tooltip: {
    marginTop: 8,
    padding: '8px 12px',
    backgroundColor: 'var(--bg-table-header)',
    borderRadius: 8,
    fontSize: 13,
  },
  th: {
    textAlign: 'left' as const, padding: '8px 12px',
    backgroundColor: 'var(--bg-table-header)', fontSize: 13, fontWeight: 600,
    color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-color)',
  },
  td: {
    padding: '8px 12px', fontSize: 13, borderBottom: '1px solid var(--border-light)',
  },
};

export default Dashboard;
